// src/lib/intelligence.ts

export type LifecycleState = 'breaking' | 'developing' | 'mature' | 'concluded' | 'contested'
export type SentimentLabel = 'positive' | 'negative' | 'neutral' | 'mixed'
export type EntityType = 'PERSON' | 'ORG' | 'GPE' | 'LOC' | 'EVENT'

export interface TrendingEntity {
  name: string
  type: EntityType
  mention_count: number
}

export interface StoryCluster {
  cluster_id: string
  title: string
  lifecycle_state: LifecycleState
  story_velocity: number
  story_source_count: number
  article_count: number
  echo_controversy_score: number | null
  echo_sentiment_label: SentimentLabel | null
  echo_dominant_themes: string[] | null
  echo_counter_narrative: boolean | null
  echo_counter_narrative_summary: string | null
  echo_novel_entities: string[] | null
  echo_platforms: string[] | null
}

export interface BriefingSignal {
  briefing_id: number
  breaking_count: number
  developing_count: number
  mature_count: number
  contested_count: number
  top_controversy: number | null
  has_counter_narrative: boolean
}

export interface DashboardIntelligence {
  stories: StoryCluster[]
  briefing_signals: Record<number, BriefingSignal>
  trending_entities: TrendingEntity[]
  summary: {
    breaking_count: number
    contested_count: number
    enriched_pct: number
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export const LIFECYCLE_CONFIG: Record<LifecycleState, {
  label: string
  color: string
  bg: string
  border: string
}> = {
  breaking: { label: 'Breaking', color: '#A32D2D', bg: '#FCEBEB', border: '#F09595' },
  developing: { label: 'Developing', color: '#854F0B', bg: '#FAEEDA', border: '#EF9F27' },
  mature: { label: 'Mature', color: '#5F5E5A', bg: '#F1EFE8', border: '#D3D1C7' },
  concluded: { label: 'Concluded', color: '#888780', bg: '#F1EFE8', border: '#D3D1C7' },
  contested: { label: 'Contested', color: '#72243E', bg: '#FBEAF0', border: '#ED93B1' },
}

export function lifecycleDotColor(state: LifecycleState): string {
  return LIFECYCLE_CONFIG[state]?.color ?? '#888780'
}

// ─── Polari L1 — pink/magenta glass, authority layer ─────────────────────────

export const POLARI_COLORS = {
  cardBg: 'linear-gradient(135deg, rgba(255,18,199,0.06) 0%, rgba(200,0,160,0.04) 50%, rgba(180,0,140,0.05) 100%)',
  cardBorderTop: '0.5px solid rgba(255,100,220,0.35)',
  cardBorderLeft: '0.5px solid rgba(255,100,220,0.18)',
  cardBorderRest: '0.5px solid rgba(200,0,160,0.1)',
  cardTopLine: 'linear-gradient(90deg, rgba(255,18,199,0.8), rgba(220,0,180,0.4), transparent 70%)',

  label: 'rgba(255,18,199,0.9)',
  labelDim: 'rgba(220,80,200,0.7)',
  dot: '#FF12C7',

  // Entity chips
  chipBg: 'rgba(255,18,199,0.07)',
  chipBorder: 'rgba(255,18,199,0.22)',
  chipColor: 'rgba(220,0,180,0.95)',
  chipDot: 'rgba(255,18,199,0.6)',
  chipCount: 'rgba(200,0,160,0.55)',

  divider: 'rgba(255,18,199,0.1)',
}

export const polariCardStyle: React.CSSProperties = {
  background: POLARI_COLORS.cardBg,
  borderTop: POLARI_COLORS.cardBorderTop,
  borderLeft: POLARI_COLORS.cardBorderLeft,
  borderRight: POLARI_COLORS.cardBorderRest,
  borderBottom: POLARI_COLORS.cardBorderRest,
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
}



export const ECHO_COLORS = {
  // Card shell — variant B: top-edge glow + tri-gradient
  cardBg: 'linear-gradient(120deg, rgba(80,180,255,0.055) 0%, rgba(100,240,200,0.03) 60%, rgba(140,80,255,0.03) 100%)',
  cardBorderTop: '0.5px solid rgba(140,210,255,0.4)',
  cardBorderLeft: '0.5px solid rgba(140,210,255,0.15)',
  cardBorderRest: '0.5px solid rgba(100,180,255,0.07)',
  cardTopLine: 'linear-gradient(90deg, rgba(140,220,255,0.7), rgba(100,240,200,0.35), transparent 65%)',

  // Echo section inside card
  sectionBg: 'linear-gradient(120deg, rgba(80,180,255,0.055) 0%, rgba(100,240,200,0.03) 60%, rgba(140,80,255,0.03) 100%)',
  sectionBorderTop: '0.5px solid rgba(140,210,255,0.4)',
  sectionBorderLeft: '0.5px solid rgba(140,210,255,0.15)',
  sectionBorderRest: '0.5px solid rgba(100,180,255,0.07)',
  sectionTopLine: 'linear-gradient(90deg, rgba(140,220,255,0.7), rgba(100,240,200,0.35), transparent 65%)',

  label: 'rgba(40,150,200,0.95)',
  trackBg: 'rgba(120,180,255,0.1)',

  // Controversy fill — scales by score
  controversyCalm: 'rgba(120,200,255,0.75)',
  controversyMid: '#EF9F27',
  controversyHigh: '#D85A30',
  controversyHot: '#A32D2D',

  // Chips
  platformBg: 'rgba(120,200,255,0.12)',
  platformBorder: 'rgba(120,200,255,0.25)',
  platformColor: 'rgba(30,120,180,0.85)',

  counterBg: 'rgba(180,100,20,0.12)',
  counterBorder: 'rgba(200,130,40,0.35)',
  counterColor: 'rgba(120,55,5,0.95)',

  themeBg: 'rgba(239,159,39,0.1)',
  themeBorder: 'rgba(239,159,39,0.22)',
  themeColor: 'rgba(120,70,5,0.9)',
}

// Neutral card — brand gradient top edge
export const CARD_COLORS = {
  borderTop: '0.5px solid var(--color-border-tertiary)',
  borderRest: '0.5px solid var(--color-border-tertiary)',
  topLine: 'linear-gradient(90deg, rgba(102,0,255,0.3), rgba(0,255,192,0.15), transparent 70%)',
}

export function controversyFillColor(score: number): string {
  if (score >= 0.8) return ECHO_COLORS.controversyHot
  if (score >= 0.65) return ECHO_COLORS.controversyHigh
  if (score >= 0.5) return ECHO_COLORS.controversyMid
  return ECHO_COLORS.controversyCalm
}

export function controversyLabel(score: number): string {
  if (score >= 0.8) return 'Very high'
  if (score >= 0.65) return 'High'
  if (score >= 0.5) return 'Moderate'
  if (score >= 0.3) return 'Low'
  return 'Minimal'
}

export function velocityLabel(velocity: number): string {
  if (velocity >= 2) return `${velocity.toFixed(1)} art/hr`
  if (velocity >= 0.5) return `${velocity.toFixed(2)} art/hr`
  return 'Slow'
}

// ─── Shared card style helpers ────────────────────────────────────────────────

export const echoCardStyle: React.CSSProperties = {
  background: ECHO_COLORS.cardBg,
  borderTop: ECHO_COLORS.cardBorderTop,
  borderLeft: ECHO_COLORS.cardBorderLeft,
  borderRight: ECHO_COLORS.cardBorderRest,
  borderBottom: ECHO_COLORS.cardBorderRest,
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
}

export const neutralCardStyle: React.CSSProperties = {
  background: 'var(--color-background-primary)',
  border: '0.5px solid var(--color-border-tertiary)',
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
}

// ─── Mock data ────────────────────────────────────────────────────────────────

export const MOCK_INTELLIGENCE: DashboardIntelligence = {
  summary: { breaking_count: 2, contested_count: 1, enriched_pct: 48 },
  trending_entities: [
    { name: 'Federal Reserve', type: 'ORG', mention_count: 14 },
    { name: 'Jerome Powell', type: 'PERSON', mention_count: 11 },
    { name: 'EU AI Act', type: 'EVENT', mention_count: 8 },
    { name: 'Nvidia', type: 'ORG', mention_count: 7 },
    { name: 'Iran', type: 'GPE', mention_count: 6 },
    { name: 'OpenAI', type: 'ORG', mention_count: 5 },
    { name: 'Strait of Hormuz', 'type': 'LOC', mention_count: 4 },
  ],
  stories: [
    {
      cluster_id: 'clus_881b50b64696',
      title: 'Federal Reserve signals third consecutive rate hold amid inflation data revision',
      lifecycle_state: 'breaking',
      story_velocity: 2.4,
      story_source_count: 8,
      article_count: 14,
      echo_controversy_score: 0.72,
      echo_sentiment_label: 'negative',
      echo_dominant_themes: ['inflation expectations', 'fed independence', 'recession risk'],
      echo_counter_narrative: true,
      echo_counter_narrative_summary: null,
      echo_novel_entities: null,
      echo_platforms: ['reddit', 'bluesky'],
    },
    {
      cluster_id: 'clus_658c5de1e226',
      title: 'US evacuates personnel from Beirut as Iran operation disrupts regional shipping',
      lifecycle_state: 'contested',
      story_velocity: 0.035,
      story_source_count: 2,
      article_count: 9,
      echo_controversy_score: 0.88,
      echo_sentiment_label: 'negative',
      echo_dominant_themes: ['oil prices', 'military escalation', 'Hormuz closure'],
      echo_counter_narrative: true,
      echo_counter_narrative_summary: null,
      echo_novel_entities: null,
      echo_platforms: ['reddit', 'bluesky'],
    },
    {
      cluster_id: 'clus_031724d7c018',
      title: 'EU AI Act enforcement body releases first compliance framework for foundation models',
      lifecycle_state: 'developing',
      story_velocity: 0.5,
      story_source_count: 3,
      article_count: 6,
      echo_controversy_score: 0.41,
      echo_sentiment_label: 'neutral',
      echo_dominant_themes: ['open source models', 'regulatory overreach'],
      echo_counter_narrative: false,
      echo_counter_narrative_summary: null,
      echo_novel_entities: null,
      echo_platforms: ['reddit'],
    },
    {
      cluster_id: 'clus_bc64eb467318',
      title: 'Nvidia announces next-generation data centre GPU with 3× memory bandwidth',
      lifecycle_state: 'mature',
      story_velocity: 0.133,
      story_source_count: 1,
      article_count: 4,
      echo_controversy_score: null,
      echo_sentiment_label: null,
      echo_dominant_themes: null,
      echo_counter_narrative: null,
      echo_counter_narrative_summary: null,
      echo_novel_entities: null,
      echo_platforms: null,
    },
  ],
  briefing_signals: {
    1: { briefing_id: 1, breaking_count: 2, developing_count: 7, mature_count: 15, contested_count: 1, top_controversy: 0.88, has_counter_narrative: true },
    2: { briefing_id: 2, breaking_count: 0, developing_count: 4, mature_count: 14, contested_count: 0, top_controversy: 0.41, has_counter_narrative: false },
  },
}

export async function fetchDashboardIntelligence(view: 'local' | 'global' = 'local'): Promise<DashboardIntelligence> {
  const { api } = await import('./api')
  return await api.get(`/api/v1/dashboard/intelligence?view=${view}`) as DashboardIntelligence
}

// need React for CSSProperties type
import type React from 'react'